import { useInfiniteQuery } from "@tanstack/react-query";
import { z } from "zod";
import { PlaylistThumb } from "@web/components/PlaylistThumb.tsx";
import { EmptyState } from "@web/components/EmptyState.tsx";
import { Button } from "@web/components/ui/button.tsx";
import { Skeleton } from "@web/components/ui/skeleton.tsx";
import { useOpenDetail } from "@web/features/detail/useOpenDetail.ts";
import { api } from "@web/lib/api.ts";
import { formatDate } from "@web/lib/format.ts";

const PAGE = 40;

const ArtistPlaylistsPage = z.object({
  playlists: z.array(
    z.object({
      kind: z.enum(["playlist", "local"]),
      id: z.string(),
      name: z.string().nullable(),
      image_url: z.string().nullable(),
      track_count: z.number(),
      last_added_at: z.string().nullable(),
    }),
  ),
  total: z.number(),
});

/** Artist sheet — Spotify + local playlists, newest artist-add first, paginated. */
export function ArtistPlaylistsSection({ artistId }: { artistId: string }) {
  const playlists = useInfiniteQuery({
    queryKey: ["artists", artistId, "playlists"],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const sp = new URLSearchParams({
        limit: String(PAGE),
        offset: String(pageParam),
      });
      return ArtistPlaylistsPage.parse(
        await api(`/api/artists/${encodeURIComponent(artistId)}/playlists?${sp}`),
      );
    },
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.playlists.length, 0);
      return loaded < last.total ? loaded : undefined;
    },
  });
  const openDetail = useOpenDetail();

  if (playlists.isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (playlists.isError) {
    return (
      <EmptyState
        title="Couldn't load playlists"
        description={
          playlists.error instanceof Error ? playlists.error.message : "Unknown error"
        }
      />
    );
  }

  const list = playlists.data?.pages.flatMap((p) => p.playlists) ?? [];
  const total = playlists.data?.pages[0]?.total ?? 0;

  if (list.length === 0) {
    return (
      <EmptyState
        title="No playlists"
        description="None of your Spotify or local playlists include this artist yet."
      />
    );
  }

  return (
    <div className="space-y-2">
      <p className="px-1 text-xs text-faint">
        {total.toLocaleString()} playlist{total === 1 ? "" : "s"} · newest add first
      </p>
      <ul className="divide-y divide-line">
        {list.map((pl) => {
          const detailType = pl.kind === "local" ? "local_playlist" : "playlist";
          const kindLabel = pl.kind === "local" ? "Local" : "Spotify";
          const when = pl.last_added_at ? formatDate(pl.last_added_at) : null;
          return (
            <li key={`${pl.kind}:${pl.id}`}>
              <button
                type="button"
                className="flex w-full items-center gap-3 px-1 py-2 text-left hover:bg-row-hover"
                onClick={() =>
                  openDetail(
                    {
                      type: detailType,
                      id: pl.id,
                      label: pl.name ?? "Playlist",
                    },
                    { mode: "push" },
                  )
                }
              >
                <PlaylistThumb coverUrl={pl.image_url} alt="" className="size-9" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">
                    {pl.name ?? "Untitled playlist"}
                  </span>
                  <span className="block truncate text-xs text-faint">
                    {kindLabel} · {pl.track_count.toLocaleString()} of theirs
                    {when && when !== "—" ? ` · ${when}` : ""}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {playlists.hasNextPage && (
        <div className="px-1 pt-1">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={playlists.isFetchingNextPage}
            onClick={() => void playlists.fetchNextPage()}
          >
            {playlists.isFetchingNextPage
              ? "Loading…"
              : `Show more (${(total - list.length).toLocaleString()} left)`}
          </Button>
        </div>
      )}
    </div>
  );
}
